import Keycloak from 'keycloak-js';

import configs from '../configs';
import { INTERVAL_TIME_REFRESH_TOKEN } from '../helpers/constant';
import { getStorage, setStorage, STORAGE_KEY } from '../helpers/storage';

let intervalRefresh: NodeJS.Timeout | null = null;

// Initialize Keycloak instance only if config is available
let keycloak: Keycloak | null = null;

if (configs.base.KEYCLOAK_URL && configs.base.KEYCLOAK_REALM && configs.base.KEYCLOAK_CLIENT_ID) {
  keycloak = new Keycloak({
    url: configs.base.KEYCLOAK_URL,
    realm: configs.base.KEYCLOAK_REALM,
    clientId: configs.base.KEYCLOAK_CLIENT_ID,
  });
}

class AuthInstance {
  userProfile: any = null;
  roles: any = null;
  accessToken: string | null = null;
  keycloak = keycloak;

  syncAuth = async () => {
    try {
      // Only use existing token from localStorage
      const existingToken = getStorage(STORAGE_KEY.TOKEN);
      const existingUser = getStorage(STORAGE_KEY.USER);

      if (existingToken && existingUser) {
        this.accessToken = existingToken;
        this.userProfile = JSON.parse(existingUser);
        return this.userProfile;
      }

      // If no existing token, try Keycloak
      if (this.keycloak && this.keycloak.authenticated) {
        return await this.loadUserProfile();
      }

      // No authentication available
      throw new Error('No authentication method available');
    } catch (error: any) {
      console.warn('Authentication failed:', error);
      throw error;
    }
  };

  forceRefreshToken = async () => {
    // Use Keycloak token refresh instead
    if (this.keycloak && this.keycloak.authenticated) {
      try {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          console.log('Token refreshed successfully');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }
  };

  // Initialize Keycloak authentication
  initKeycloak = async () => {
    if (!this.keycloak) {
      console.warn('Keycloak is not configured, falling back to OAuth2');
      return null;
    }

    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
      });

      if (authenticated) {
        this.accessToken = this.keycloak.token;
        await this.loadUserProfile();
        this.refreshNewToken();
        return this.userProfile;
      }
      return null;
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      // Fallback to OAuth2 if Keycloak fails
      return null;
    }
  };

  // Load user profile from Keycloak
  loadUserProfile = async () => {
    if (!this.keycloak) {
      return null;
    }

    try {
      const profile = await this.keycloak.loadUserProfile();
      this.userProfile = profile;

      // Extract roles from token
      if (this.keycloak?.token) {
        const base64Payload = this.keycloak.token.split('.')[1];
        const payload = JSON.parse(atob(base64Payload));
        this.roles = payload?.realm_access?.roles || [];

        // Store in localStorage for compatibility
        setStorage(STORAGE_KEY.USER, JSON.stringify(profile));
        setStorage(STORAGE_KEY.TOKEN, this.keycloak.token);
        setStorage(STORAGE_KEY.ID_TOKEN, this.keycloak.token);
        setStorage(STORAGE_KEY.ID, profile.id);
      }

      return profile;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }
  };

  // Login using Keycloak
  login = () => {
    if (!this.keycloak) {
      // Fallback to OAuth2 login
      window.location.href = '/oauth2/start';
      return;
    }
    return this.keycloak.login();
  };

  // Logout using Keycloak
  logoutKeycloak = () => {
    if (!this.keycloak) {
      return;
    }
    this.stopSync();
    return this.keycloak.logout({
      redirectUri: window.location.origin,
    });
  };

  // Check if user is authenticated
  isAuthenticated = () => {
    return this.keycloak ? this.keycloak.authenticated : false;
  };

  // Get current token
  getToken = () => {
    return this.keycloak ? this.keycloak.token : null;
  };

  // Refresh token
  refreshToken = async () => {
    if (!this.keycloak) {
      return false;
    }

    try {
      const refreshed = await this.keycloak.updateToken(30);
      if (refreshed) {
        this.accessToken = this.keycloak.token || null;
        setStorage(STORAGE_KEY.TOKEN, this.keycloak.token);
        setStorage(STORAGE_KEY.ID_TOKEN, this.keycloak.token);
      }
      return refreshed;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  };

  initialAuth = async () => {
    try {
      // Check if we already have valid token in localStorage
      const existingToken = getStorage(STORAGE_KEY.TOKEN);
      const existingUser = getStorage(STORAGE_KEY.USER);

      if (existingToken && existingUser) {
        console.log('Using existing token from localStorage');
        this.accessToken = existingToken;
        this.userProfile = JSON.parse(existingUser);
        this.refreshNewToken();
        return this.userProfile;
      }

      // Try Keycloak first
      console.log('Attempting Keycloak initialization...');
      const keycloakResult = await this.initKeycloak();
      if (keycloakResult) {
        console.log('Keycloak authentication successful');
        return keycloakResult;
      }

      // If Keycloak fails, don't try OAuth2 as it's not available
      console.log('Keycloak failed, no fallback available');
      return null;
    } catch (error) {
      console.error('Authentication failed:', error);
      return null;
    }
  };

  refreshNewToken = () => {
    intervalRefresh = setInterval(async () => {
      // Try Keycloak token refresh first
      if (this.keycloak && this.keycloak.authenticated) {
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          // If refresh failed, fallback to OAuth2 method
          this.syncAuth();
        }
      } else {
        // Fallback to OAuth2 method
        this.syncAuth();
      }
    }, INTERVAL_TIME_REFRESH_TOKEN);
  };

  logout = async () => {
    this.stopSync();
    try {
      // Try Keycloak logout first
      if (this.keycloak && this.keycloak.authenticated) {
        return this.logoutKeycloak();
      }

      // Fallback to direct Keycloak logout URL
      const logoutURL = `${configs.base.KEYCLOAK_URL}/realms/${
        configs.base.KEYCLOAK_REALM
      }/protocol/openid-connect/logout?pre_locale=${
        getStorage(STORAGE_KEY.I18_NEXT_LANGUAGE) ?? ''
      }&client_id=${configs.base.KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${
        window.location.origin
      }`;

      window.location.href = logoutURL;
    } catch (error) {
      // Clear local storage and redirect to home
      localStorage.clear();
      window.location.href = '/';
      throw error;
    }
  };

  stopSync = () => {
    setStorage(STORAGE_KEY.TOKEN, '');
    if (intervalRefresh) {
      clearInterval(intervalRefresh);
    }
    this.userProfile = null;
    this.roles = null;
    this.accessToken = null;
  };
}

const authInstance = new AuthInstance();
export default authInstance;
export { authInstance };
